<template>
  <BaseHtmlForm
    ref="paymentRequestForm"
    :action="store.paymentRequestFormAction"
    method="POST"
    enctype="multipart/form-data"
  >
    <input type="hidden" name="thread_id" :value="store.threadId" />
    <template
      v-if="
        store.paymentRequestRate.rateType === RateType.FIXED ||
        store.paymentRequestRate.rateType !== store.hireRate.rateType ||
        store.paymentRequestRate.rate !== store.hireRate.rate
      "
    >
      <input type="hidden" name="rate" :value="store.paymentRequestRate.rate" />
    </template>
    <template v-else>
      <input type="hidden" name="rate" :value="store.hireRate.rate" />
    </template>
    <input
      type="hidden"
      name="ratetype"
      :value="
        store.hireRate.rateType === RateType.MONTHLY
          ? RateType.FIXED
          : store.paymentRequestRate.rateType
      "
    />
    <input type="hidden" name="bill_id" :value="store.billId" />
    <input
      v-if="store.paymentRequestType === PaymentRequestTypeEnum.MILESTONE"
      type="hidden"
      name="milestone_id"
      :value="store.paymentRequestForm.milestoneId"
    />
    <input
      v-if="timesheetsStore.timesheetType.value === TimesheetTypeEnum.TIMESHEET"
      type="hidden"
      :name="billControllerTimeUnitName"
      :value="calculationsStore.baseTimeAmount"
    />
    <input
      v-else
      type="hidden"
      :name="billControllerTimeUnitName"
      :value="store.paymentRequestForm.billableTime"
    />

    <input
      v-if="dateStartToBillControllerFormat"
      type="hidden"
      name="date_start"
      :value="dateStartToBillControllerFormat"
    />
    <input
      v-if="dateEndToBillControllerFormat"
      type="hidden"
      name="date_end"
      :value="dateEndToBillControllerFormat"
    />
    <!--  Seems unused on backend, but it is included in the old bill modal request and equals the 'total' field  -->
    <input
      v-if="store.paymentRequestRate.rateType === RateType.FIXED"
      type="hidden"
      name="total_fixed"
      :value="store.paymentRequestForm.billableTotal"
    />
    <!--  Fixed rate bills actually use the total to store the input value  -->
    <input
      type="hidden"
      name="total"
      :value="
        store.paymentRequestRate.rateType === RateType.FIXED
          ? store.paymentRequestForm.billableTotal
          : calculationsStore.totalChargedNumber
      "
    />
    <input type="hidden" name="timesheet_type" :value="timesheetsStore.timesheetType.value" />
    <input
      type="hidden"
      name="timesheet_id"
      :value="
        timesheetsStore.timesheetType.value === TimesheetTypeEnum.TIMESHEET
          ? timesheetsStore.worksomeTimesheetId
          : null
      "
    />
    <input
      v-if="timesheetsStore.timesheetType.value !== TimesheetTypeEnum.NONE"
      type="hidden"
      name="timesheet_confirmed"
      :value="store.paymentRequestForm.timesheetConfirmed"
    />
    <input
      type="hidden"
      name="timesheet_id"
      :value="
        timesheetsStore.timesheetType.value === TimesheetTypeEnum.TIMESHEET
          ? timesheetsStore.worksomeTimesheetId
          : null
      "
    />
    <input
      v-if="timesheetsStore.timesheetType.value === TimesheetTypeEnum.TIMESHEET"
      type="hidden"
      name="timesheet_confirmed"
      :value="store.paymentRequestForm.timesheetConfirmed"
    />
    <input
      type="hidden"
      name="timesheet_data"
      :value="
        timesheetsStore.timesheetType.value === TimesheetTypeEnum.TEXT
          ? timesheetsStore.timesheetComment
          : null
      "
    />
    <input
      type="hidden"
      name="timesheet_jexcel"
      :value="
        timesheetsStore.timesheetType.value === TimesheetTypeEnum.SHEET
          ? timesheetsStore.timesheetSpreadsheetValue
          : null
      "
    />
    <template v-if="filesToAttachToPaymentRequest.length > 0">
      <input
        v-for="timesheetFile in filesToAttachToPaymentRequest"
        :key="timesheetFile.localId"
        type="hidden"
        name="uploadfiles[]"
        :value="timesheetFile.localId"
      />
    </template>
    <template v-else>
      <input type="hidden" name="uploadfiles[]" :value="null" />
    </template>
    <input v-if="store.vatRate > 0" type="hidden" name="usevat" :value="1" />
    <template v-else>
      <!-- eslint-disable-next-line vue/require-v-for-key -- Baseline eslint ignore please fix if working on this-->
      <input
        v-for="taxLine in taxStore.taxLines"
        type="hidden"
        :name="`taxLines[${taxLine.terminology}]`"
        :value="taxLine.rate"
      />
    </template>
    <!-- eslint-disable-next-line vue/require-v-for-key -- Baseline eslint ignore please fix if working on this-->
    <input
      v-for="expenseReportLocalId in expensesStore.expenseReportLocalIds"
      type="hidden"
      name="expenseReports[]"
      :value="expenseReportLocalId"
    />
    <input type="hidden" name="message" :value="store.paymentRequestForm.comments" />
    <!--    AddBillTaxLinesDTO-->
    <WdsStack gap-sm>
      <WdsButton
        v-if="['unsaved', 'draft', null].includes(store.paymentRequestStatus)"
        tertiary
        :disabled="store.isSubmitting"
        @click.prevent="saveDraftPaymentRequest"
        class="tw-whitespace-nowrap"
      >
        {{ $t('Save draft') }}
      </WdsButton>
      <WdsButton
        primary
        :disabled="store.isSubmitting"
        data-test="payment-request-submit-button"
        @click.prevent="sendPaymentRequest"
        class="tw-whitespace-nowrap"
      >
        {{
          store.paymentRequestStatus === 'issued'
            ? $t('Save payment request')
            : $t('Send payment request')
        }}
      </WdsButton>
    </WdsStack>
  </BaseHtmlForm>
</template>

<script lang="ts" setup>
import { WdsButton, WdsStack } from '@worksome/wds-components'
import { computed, ref, watchEffect } from 'vue'
import moment from 'moment/moment'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'
import { RateType } from '@/enums'
import { TimesheetTypeEnum, PaymentRequestTypeEnum } from '@/components/PaymentRequest/enums'
import { useTimesheetStore } from '@/components/PaymentRequest/store/useTimesheetStore'
import { useValidationStore } from '@/components/PaymentRequest/store/useValidationStore'
import { useCalculationsStore } from '@/components/PaymentRequest/store/useCalculationsStore'
import { useExpensesStore } from '@/components/PaymentRequest/store/useExpensesStore'
import { useTaxStore } from '@/components/PaymentRequest/store/useTaxStore'
import { useTranslation } from '@/composables/translation'
import { useHtmlFormStore } from '@/components/PaymentRequest/store/useHtmlFormStore'
import { useTimesheetParsingStore } from '@/components/Timesheet/store/useTimesheetParsingStore'

const htmlFormStore = useHtmlFormStore()
const paymentRequestForm = ref<HTMLFormElement | null>(null)
// Place the form's template ref into the store to be used in the
// submitForm method here and outside of this component
watchEffect(() => {
  if (paymentRequestForm.value) {
    htmlFormStore.paymentRequestFormRef = paymentRequestForm.value
  }
})
const { $t } = useTranslation()
const store = usePaymentRequestStore()
const timesheetsStore = useTimesheetStore()
const validationStore = useValidationStore()
const calculationsStore = useCalculationsStore()
const expensesStore = useExpensesStore()
const taxStore = useTaxStore()
const timesheetParsingStore = useTimesheetParsingStore()

const saveDraftPaymentRequest = async () => {
  // @ts-ignore
  window.unsaved = false
  if (!paymentRequestForm.value || store.isSubmitting) return
  store.isSubmitting = true
  validationStore.v$.$reset()
  await htmlFormStore.submitForm('/bills/draft')
}
const sendPaymentRequest = async () => {
  // @ts-ignore
  window.unsaved = false
  if (!paymentRequestForm.value || store.isSubmitting) return
  const isFormCorrect = await validationStore.v$.$validate()
  if (!isFormCorrect) return
  store.isSubmitting = true
  await htmlFormStore.submitForm('/bills')
}

defineExpose({ submitForm: () => {} })

const dateStartToBillControllerFormat = computed(() =>
  store.paymentRequestForm.dateStart
    ? moment(store.paymentRequestForm.dateStart, 'YYYY-MM-DD').format('DD/MM/YYYY')
    : null,
)
const dateEndToBillControllerFormat = computed(() =>
  store.paymentRequestForm.dateEnd
    ? moment(store.paymentRequestForm.dateEnd, 'YYYY-MM-DD').format('DD/MM/YYYY')
    : null,
)

const billControllerTimeUnitName = computed(() => {
  switch (store.paymentRequestRate.rateType) {
    case RateType.HOURLY:
      return 'hours'
    case RateType.DAILY:
      return 'days'
    case RateType.WEEKLY:
      return 'weeks'
    case RateType.MONTHLY:
      return 'months'
  }
  return 'hours'
})

const filesToAttachToPaymentRequest = computed(() =>
  timesheetsStore.timesheetType.value === TimesheetTypeEnum.FILE
    ? [...timesheetsStore.timesheetFiles]
    : [...timesheetParsingStore.filesToAttachToPaymentRequest],
)
</script>
